import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { LeaveListItem, LeaveTypeId } from '@lms/shared-types'
import { Card } from '../primitives/Card'
import { Segmented } from '../primitives/Segmented'
import { ThinkingDots } from '../primitives/ThinkingDots'
import { getCachedTeamPatterns } from '../../api/patterns'
import { flags } from '../../lib/feature-flags'
import {
  isDecideToday,
  pickTopAnomaly,
  pickTopBadge,
  pickWorstTightDay,
  type RowBadge,
  type TightDayInput,
} from '../../lib/manager-queue'
import { ManagerQueueRow } from './ManagerQueueRow'

type QueueFilter = 'decide-today' | 'all' | 'emergency'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function shortWeekday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return ''
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return WEEKDAY_LABELS[dow] ?? ''
}

function holidayChipLabel(adj: NonNullable<LeaveListItem['holidayAdjacency']>): string {
  // "straddles" reads loudest — the leave wraps the holiday entirely.
  // "before" / "after" describe the leave's position relative to the holiday.
  switch (adj.position) {
    case 'straddles':
      return `Straddles ${adj.holidayName}`
    case 'before':
      return `Day before ${adj.holidayName}`
    case 'after':
      return `Day after ${adj.holidayName}`
  }
}

export interface ManagerQueueCardProps {
  leaves: ReadonlyArray<LeaveListItem>
  weekDays?: ReadonlyArray<TightDayInput> | undefined
  leaveTypeMeta: Record<LeaveTypeId, { label: string; color: string }>
  onRefetch: () => void
}

/**
 * The manager's queue — full list of pending team requests with inline
 * approve + ⋯-menu actions. The card pre-resolves the single row badge
 * via pickTopBadge so the row stays presentational; the priority chain
 * (emergency > blackout > cluster > tight > holiday > anomaly) lives
 * in one place.
 *
 * Replaces the 5-row "Queue preview" card from the previous dashboard:
 * Hafiz never wanted a preview, he wanted the queue.
 */
export function ManagerQueueCard({
  leaves,
  weekDays,
  leaveTypeMeta,
  onRefetch,
}: ManagerQueueCardProps): JSX.Element {
  const patternsQuery = useQuery({
    queryKey: ['patterns', 'team', 'manager-queue'],
    queryFn: () => getCachedTeamPatterns(),
    staleTime: 5 * 60 * 1000,
  })

  const days = weekDays ?? []

  // Pre-compute the single badge for each row. Wave 3 will populate the
  // blackout/cluster/holiday inputs from server fields — until then they
  // stay null and the priority chain falls through to tight/anomaly.
  const predictiveChipsActive = flags.managerRowPredictiveChips
  const segmentedActive = flags.managerDecideTodaySegmented

  // Bucket counts for the segmented control. Computed once per leaves
  // array (cheap — just a couple of filters). Anchor on "today" so the
  // boundary doesn't drift across re-renders inside the same session.
  const today = useMemo(() => new Date(), [leaves])
  const decideTodayCount = useMemo(
    () =>
      leaves.filter((l) =>
        isDecideToday({ submittedAt: l.createdAt, startDate: l.startDate }, today),
      ).length,
    [leaves, today],
  )
  const emergencyCount = useMemo(
    () => leaves.filter((l) => l.isEmergency).length,
    [leaves],
  )

  // Default filter selection per Hafiz: land on "Decide today" when it
  // has rows; otherwise "All" so the screen isn't empty.
  const [filter, setFilter] = useState<QueueFilter>(() =>
    decideTodayCount > 0 ? 'decide-today' : 'all',
  )

  const visibleLeaves = useMemo(() => {
    if (!segmentedActive || filter === 'all') return leaves
    if (filter === 'emergency') return leaves.filter((l) => l.isEmergency)
    // decide-today
    return leaves.filter((l) =>
      isDecideToday({ submittedAt: l.createdAt, startDate: l.startDate }, today),
    )
  }, [leaves, filter, segmentedActive, today])

  const decorated = useMemo<ReadonlyArray<{ leave: LeaveListItem; badge: RowBadge | null }>>(
    () =>
      visibleLeaves.map((leave) => {
        const anomaly = pickTopAnomaly(
          leave.employee.id,
          { type: leave.type, isEmergency: leave.isEmergency },
          patternsQuery.data,
        )
        const tightDay = pickWorstTightDay(leave.startDate, leave.endDate, days)
        // Predictive chips are gated FE-side. Server always populates the
        // fields; we just don't render them until the flag is on for this
        // role/env. pickTopBadge tolerates null inputs.
        const blackout = predictiveChipsActive && leave.blackoutProximity
          ? {
              label:
                leave.blackoutProximity.daysUntil === 0
                  ? `Same day as ${leave.blackoutProximity.name}`
                  : `Ends ${leave.blackoutProximity.daysUntil}d before ${leave.blackoutProximity.name}`,
              daysUntil: leave.blackoutProximity.daysUntil,
            }
          : null
        const cluster = predictiveChipsActive && leave.clusterImpact
          ? {
              label: `Would make ${shortWeekday(leave.clusterImpact.date)} ${leave.clusterImpact.headcount}/${leave.clusterImpact.slotsTotal}`,
              date: leave.clusterImpact.date,
            }
          : null
        const holiday = predictiveChipsActive && leave.holidayAdjacency
          ? {
              label: holidayChipLabel(leave.holidayAdjacency),
              holidayName: leave.holidayAdjacency.holidayName,
            }
          : null
        const badge = pickTopBadge({
          isEmergency: leave.isEmergency,
          blackout,
          cluster,
          tightDay,
          holiday,
          anomaly,
        })
        return { leave, badge }
      }),
    [visibleLeaves, patternsQuery.data, days, predictiveChipsActive],
  )

  return (
    <Card padding="14px 16px" data-testid="manager-queue-card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 10,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {leaves.length === 0
            ? 'Queue'
            : `Queue · ${leaves.length} ${leaves.length === 1 ? 'request' : 'requests'}`}
        </h2>
        {patternsQuery.isLoading ? (
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            <ThinkingDots label="Loading signals" />
          </span>
        ) : null}
      </div>

      {segmentedActive && leaves.length > 0 ? (
        <div data-testid="queue-filter" style={{ marginBottom: 10 }}>
          <Segmented
            options={[
              { value: 'decide-today', label: `Decide today · ${decideTodayCount}` },
              { value: 'all', label: `All · ${leaves.length}` },
              { value: 'emergency', label: `Emergency · ${emergencyCount}` },
            ]}
            value={filter}
            onChange={(next) => setFilter(next as QueueFilter)}
            ariaLabel="Filter queue"
          />
        </div>
      ) : null}

      {leaves.length === 0 ? (
        <div data-testid="manager-queue-empty" style={{ fontSize: 13, color: 'var(--text-3)' }}>
          Nothing waiting on you.
        </div>
      ) : decorated.length === 0 ? (
        <div data-testid="manager-queue-filtered-empty" style={{ fontSize: 13, color: 'var(--text-3)' }}>
          {filter === 'decide-today'
            ? 'Nothing needs a decision today.'
            : filter === 'emergency'
              ? 'No emergency requests.'
              : 'Nothing waiting on you.'}
        </div>
      ) : (
        <ul role="list" style={{ display: 'flex', flexDirection: 'column' }}>
          {decorated.map(({ leave, badge }) => (
            <ManagerQueueRow
              key={leave.id}
              leave={leave}
              badge={badge}
              leaveTypeMeta={leaveTypeMeta}
              onDecided={onRefetch}
            />
          ))}
        </ul>
      )}
    </Card>
  )
}
